import { cn } from '~/utils'
import { isPaywalled } from '../features/billing/utils/plan'
import { useState, type PropsWithChildren } from 'react'
import { Link, useParams } from '@tanstack/react-router'
import { useSuspenseQuery } from '../services'
import { Button } from '@bpinternal/ui-kit-next'
import { capitalize } from 'lodash'
import { trackEvent, type PaywallAnalyticEventName } from '../providers/SegmentProvider'
import { Plan } from '~/features/billing/types'

type Props = PropsWithChildren<{ className?: string; minimumPlan: Plan; analyticsEventName?: PaywallAnalyticEventName }>

export const Paywall = ({ className, children, minimumPlan, analyticsEventName, ...props }: Props) => {
  const { workspaceId } = useParams({ from: '/workspaces/$workspaceId' })
  const { plan } = useSuspenseQuery('workspaces_/$workspaceId_', { workspaceId }).data
  const isBlocked = isPaywalled(plan, minimumPlan)
  const [isActive, setIsActive] = useState(false)

  return (
    <div
      {...props}
      className={cn(className, 'group relative grid place-items-center')}
      onClick={() => isBlocked && setIsActive(true)}
    >
      {isBlocked && (
        <div
          className={cn(
            'absolute -inset-4 z-10 col-start-1 row-start-1 flex items-center justify-center overflow-clip rounded-md border-[1px] border-gray-300 opacity-0 backdrop-blur-0 transition-all group-hover:opacity-100 group-hover:backdrop-blur-sm',
            isActive && 'opacity-100 backdrop-blur-sm'
          )}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.stopPropagation()
              setIsActive(false)
            }
          }}
        >
          <div
            className="flex flex-col items-center justify-center "
            onClick={(e) => {
              if (!isActive) {
                e.preventDefault()
                e.stopPropagation()
              }
            }}
          >
            <Button
              asChild
              variant="outline"
              className="shadow-none"
              onClick={() =>
                analyticsEventName &&
                trackEvent({ type: 'paywall_triggered', feature: analyticsEventName, fromPlan: plan, workspaceId })
              }
            >
              <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
                Upgrade
              </Link>
            </Button>
            <p className="mt-2 text-center text-sm font-medium text-neutral-800">
              Requires {capitalize(minimumPlan)}
              {minimumPlan !== 'enterprise' && ' or higher'}
            </p>
          </div>
        </div>
      )}
      <div className="col-start-1 row-start-1 w-full">{children}</div>
    </div>
  )
}
